import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  CircularProgress,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import {
  teamViewerLicenseService,
  TeamViewerChannelUsageSummary,
  TeamViewerSession,
} from '../services/teamViewerLicenseService';

export const TeamViewerChannels: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [summary, setSummary] = useState<TeamViewerChannelUsageSummary | null>(null);
  const [sessions, setSessions] = useState<TeamViewerSession[]>([]);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        setError(null);
        const [summaryData, sessionsData] = await Promise.all([
          teamViewerLicenseService.getChannelUsage(),
          teamViewerLicenseService.getSessions(),
        ]);
        setSummary(summaryData);
        setSessions(sessionsData);
      } catch (loadError: any) {
        setError(loadError?.message ?? 'Failed to load TeamViewer channels.');
      } finally {
        setLoading(false);
      }
    };

    load();
  }, []);

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        TeamViewer Channels
      </Typography>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error">{error}</Alert>
      ) : (
        <>
          {summary?.warning && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {summary.warning}
            </Alert>
          )}

          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={4}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Max Channels
                </Typography>
                <Typography variant="h5">{summary?.maxChannels ?? 0}</Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Used Channels
                </Typography>
                <Typography variant="h5">{summary?.usedChannels ?? 0}</Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Available Channels
                </Typography>
                <Typography variant="h5">{summary?.availableChannels ?? 0}</Typography>
              </Paper>
            </Grid>
          </Grid>

          <Typography variant="body2" color="text.secondary">
            Last updated:{' '}
            {summary?.retrievedAtUtc ? new Date(summary.retrievedAtUtc).toLocaleString() : '-'}
          </Typography>

          <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
            Sessions
          </Typography>
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Code</TableCell>
                  <TableCell>State</TableCell>
                  <TableCell>Online</TableCell>
                  <TableCell>Session Type</TableCell>
                  <TableCell>Group ID</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sessions.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      No sessions returned from TeamViewer.
                    </TableCell>
                  </TableRow>
                ) : (
                  sessions.map((session) => (
                    <TableRow key={session.code || `${session.groupId}-${session.state}`} hover>
                      <TableCell>{session.code || '-'}</TableCell>
                      <TableCell>{session.state || '-'}</TableCell>
                      <TableCell>{session.online ? 'Yes' : 'No'}</TableCell>
                      <TableCell>{session.supportSessionType || '-'}</TableCell>
                      <TableCell>{session.groupId || '-'}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
    </Box>
  );
};
